import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, PencilIcon, TrashIcon, ChatBubbleLeftEllipsisIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { oneOnOneService } from '@services/oneOnOneService';
import { getErrorMessage } from '@services/api';
import { useAuth } from '@context/AuthContext';
import LoadingSpinner from '@components/common/LoadingSpinner';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import type { OneOnOneNote } from '@/types/index';

const CHANNEL_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
  CHANNEL_OPTIONS.map((c) => [c.value, c.label])
);

interface NoteModalProps {
  note?: OneOnOneNote | null;
  onClose: () => void;
  onSaved: () => void;
}

function NoteModal({ note, onClose, onSaved }: NoteModalProps) {
  const isEditing = !!note;
  const [meetingDate, setMeetingDate] = useState(
    note?.meetingDate ?? format(new Date(), 'yyyy-MM-dd')
  );
  const [channel, setChannel] = useState(note?.channel ?? 'online');
  const [summary, setSummary] = useState(note?.summary ?? '');

  const createMutation = useMutation(
    () => oneOnOneService.createNote({ meetingDate, channel, summary }),
    {
      onSuccess: () => { toast.success('Note added!'); onSaved(); },
      onError: (err) => { toast.error(getErrorMessage(err, 'Failed to add note')); },
    }
  );

  const updateMutation = useMutation(
    () => oneOnOneService.updateNote(note!.id, { meetingDate, channel, summary }),
    {
      onSuccess: () => { toast.success('Note updated!'); onSaved(); },
      onError: (err) => { toast.error(getErrorMessage(err, 'Failed to update note')); },
    }
  );

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const isValid = meetingDate.trim().length > 0 && summary.trim().length >= 5;

  const handleSubmit = () => {
    if (isEditing) updateMutation.mutate();
    else createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 space-y-5">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {isEditing ? 'Edit 1:1 Note' : 'Add 1:1 Note'}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Log a summary of a one-on-one meeting you had.
          </p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Meeting date</label>
              <input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Channel</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="input text-sm"
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Summary</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What did you discuss? Any action items or follow-ups?"
              rows={5}
              className="input resize-none text-sm"
            />
            <p className={`text-xs mt-1 ${summary.trim().length > 0 && summary.trim().length < 5 ? 'text-red-500' : 'text-slate-400'}`}>
              {summary.trim().length}/5 min
              {summary.trim().length > 0 && summary.trim().length < 5 && ' — summary must be at least 5 characters'}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={isLoading}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
            className="btn-primary flex-1"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : isEditing ? 'Update Note' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteCard({
  note,
  readOnly,
  onEdit,
  onDelete,
}: {
  note: OneOnOneNote;
  readOnly?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className="card card-body space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">
            {format(new Date(note.meetingDate), 'MMM d, yyyy')}
          </span>
          {note.channel && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
              {CHANNEL_LABELS[note.channel] ?? note.channel}
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Edit note"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              aria-label="Delete note"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <p className="text-sm text-slate-600 whitespace-pre-wrap">{note.summary}</p>
    </div>
  );
}

function TeamMemberNotes({ user, notes }: { user: { id: number; name: string; email: string; role: string }; notes: OneOnOneNote[] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center flex-shrink-0">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-900">{user.name}</p>
            <p className="text-xs text-slate-400">{user.email}</p>
          </div>
        </div>
        <span className="text-xs text-slate-500">
          {notes.length} note{notes.length === 1 ? '' : 's'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-slate-50/50">
          {notes.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No 1:1 notes logged yet.</p>
          ) : (
            notes.map((note) => <NoteCard key={note.id} note={note} readOnly />)
          )}
        </div>
      )}
    </div>
  );
}

export default function OneOnOnePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [modalNote, setModalNote] = useState<OneOnOneNote | null | undefined>(undefined);

  const isManagerLike = user?.role === 'manager' || user?.role === 'tech_lead';

  const myNotesQuery = useQuery('my-one-on-one-notes', () => oneOnOneService.getMyNotes());
  const teamNotesQuery = useQuery(
    'team-one-on-one-notes',
    () => oneOnOneService.getTeamNotes(),
    { enabled: isManagerLike }
  );

  const deleteMutation = useMutation((id: string) => oneOnOneService.deleteNote(id), {
    onSuccess: () => {
      toast.success('Note removed');
      queryClient.invalidateQueries('my-one-on-one-notes');
    },
    onError: (err) => { toast.error(getErrorMessage(err, 'Failed to remove note')); },
  });

  const myNotes = myNotesQuery.data ?? [];
  const teamEntries = teamNotesQuery.data ?? [];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">1:1 Notes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Log summaries of your one-on-one meetings, whenever and however they happened.
          </p>
        </div>
        <button onClick={() => setModalNote(null)} className="btn-primary flex items-center gap-1.5">
          <PlusIcon className="w-4 h-4" />
          Add Note
        </button>
      </div>

      {/* My Notes */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-slate-400" />
          My Notes
        </h2>
        {myNotesQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner label="Loading notes..." />
          </div>
        ) : myNotesQuery.isError ? (
          <div className="card p-8 text-center text-slate-500 text-sm">Failed to load notes.</div>
        ) : myNotes.length === 0 ? (
          <div className="card card-body text-center py-12 text-slate-400">
            <ChatBubbleLeftEllipsisIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No 1:1 notes yet. Add one after your next meeting.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {myNotes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onEdit={() => setModalNote(note)}
                onDelete={() => deleteMutation.mutate(note.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Team Notes */}
      {isManagerLike && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <UserGroupIcon className="w-4 h-4 text-slate-400" />
            Team Notes
          </h2>
          {teamNotesQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner label="Loading team notes..." />
            </div>
          ) : teamNotesQuery.isError ? (
            <div className="card p-8 text-center text-slate-500 text-sm">Failed to load team notes.</div>
          ) : teamEntries.length === 0 ? (
            <div className="card card-body text-center py-12 text-slate-400">
              <UserGroupIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No team members found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {teamEntries.map((entry) => (
                <TeamMemberNotes key={entry.user.id} user={entry.user} notes={entry.notes} />
              ))}
            </div>
          )}
        </section>
      )}

      {modalNote !== undefined && (
        <NoteModal
          note={modalNote}
          onClose={() => setModalNote(undefined)}
          onSaved={() => {
            setModalNote(undefined);
            queryClient.invalidateQueries('my-one-on-one-notes');
          }}
        />
      )}
    </div>
  );
}
